import { csrfToken } from "@/lib/orderActions"
import { directUpload } from "@/lib/directUpload"

// Client API layer for the "Post a Project" wizard (Atrium Stage 7) — the bridge
// between the React posting builder and the Rails session endpoints under /post.
//
// Auth: every call rides the standard customer cookie session
// (Customer::BaseController → check_is_customer? + check_customer_access). There
// is no JWT here. Rails forgery protection guards all mutations, so each non-GET
// carries X-CSRF-Token pulled from <meta name="csrf-token"> (reusing
// orderActions.csrfToken()).
//
// Wire shape: the wizard's serialize.js hands these functions a nested object
// already namespaced as { project_posting: {...} }. We send it as a JSON body
// (Content-Type: application/json) rather than form-encoding —
// `params.require(:project_posting)` reads nested JSON params just fine, and it
// keeps deep per-service attribute groups (*_posting_attributes) clean.
//
// One transport, by intent: every call here is a fetch XHR, never an Inertia
// visit. Each caller stays on its own page and reacts to what comes back —
// create/autosave/price/discount hand the wizard a fresh draft or estimate,
// submit keeps a 422 on the review stage instead of re-rendering the page over
// the client-held state, and discard lets each surface refresh its own props.
// Navigation is the caller's job (the Inertia visit to /post/:id after create,
// or to the path submit hands back). An Inertia visit can't accept these
// responses at all: @inertiajs/core treats anything without an x-inertia header
// as invalid and renders its body in a near-fullscreen
// <dialog id="inertia-error-dialog">, so a bodiless 204 becomes a blank modal
// that only a backdrop click dismisses.
//
// Posting is FREE. submit mints OPEN orders + a Project and lands on /projects —
// there is no checkout step anywhere in this flow.

// Read either error envelope the controllers emit: `{ errors: [...] }` (AR full
// messages / mutual-exclusion) or `{ error: "..." }` (price endpoint). Falls back
// to a generic line when the body is empty (e.g. a bare 404).
function extractError(data) {
  if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(" ")

  if (data?.error) return data.error

  return "Something went wrong. Please try again."
}

// Parse + error-check a response for both transports. On a non-2xx it throws an
// Error carrying the extracted message and the HTTP status (callers branch on
// `.status` where they need to — e.g. 404 vs 422).
async function handleResponse(res) {
  const data = await res.json().catch(() => ({}))

  if (!res.ok) {
    const err = new Error(extractError(data))
    err.status = res.status
    err.data = data
    throw err
  }

  return data
}

// Single JSON transport for the on-page mutations. Sets the method + CSRF/JSON
// headers and serializes the body.
async function postJson(path, method, body) {
  const res = await fetch(path, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  return handleResponse(res)
}

// Does a nested payload carry a File blob? Only the autosave (saveDraft) ever
// does, and only then do we upload before saving.
function containsFile(value) {
  if (typeof File !== "undefined" && value instanceof File) return true
  if (Array.isArray(value)) return value.some(containsFile)
  if (value && typeof value === "object") return Object.values(value).some(containsFile)
  return false
}

// Copy a nested payload with every File sent straight to storage and replaced
// by its signed id. Rails attaches a signed id under the same has_one_attached
// key, and the save stays a small JSON request under Cloudflare's 100 MB cap.
async function uploadFiles(value) {
  if (typeof File !== "undefined" && value instanceof File) return directUpload(value)
  if (Array.isArray(value)) return Promise.all(value.map(uploadFiles))

  if (value && typeof value === "object") {
    const entries = await Promise.all(
      Object.entries(value).map(async ([key, item]) => [key, await uploadFiles(item)]),
    )
    return Object.fromEntries(entries)
  }

  return value
}

// Create the draft. `payload` is { project_posting: {...} } from serializeForSave
// — at create it carries project_name plus either bundle/bundle_tier (bundle
// path) or the service booleans (BYO path). Resolves to the parsed draft object
// (the `draft` shape, including `id`); the caller then Inertia-visits /post/:id.
// 422 (mutual-exclusion or invalid bundle_tier) throws with the joined errors.
export async function createDraft(payload) {
  return postJson("/post/drafts", "POST", payload)
}

// Autosave the wizard state. Send any subset of project_posting[...]; the
// controller update!s then re-syncs child postings. A selected File goes
// straight to storage first and the save carries its signed id under the
// legacy has_one_attached field name, so the blob attaches to its posting.
// Resolves to the fresh draft on 200. 422 throws the joined validation errors;
// 404 (draft not owned / gone) throws a clear message instead of the empty-body
// default.
export async function saveDraft(draftId, payload) {
  const path = `/post/drafts/${draftId}`

  try {
    const body = containsFile(payload) ? await uploadFiles(payload) : payload

    return await postJson(path, "PATCH", body)
  } catch (err) {
    if (err.status === 404) {
      throw new Error("This draft no longer exists. Start a new project.")
    }

    throw err
  }
}

// Final submit — the revenue event. A fetch XHR (NOT an Inertia visit) so a
// validation failure keeps the wizard on the review stage with its client state
// intact: on success the server runs StepValidations + create_order, sets the
// "posted" flash, and returns { redirect } for the caller to client-navigate to;
// on 422 it returns { errors: [...] } which the caller surfaces in the review
// screen (err.data.errors) without leaving the page. No body beyond CSRF needed.
export async function submitProject(draftId) {
  return postJson(`/post/drafts/${draftId}/submit`, "POST")
}

// Live cost estimate — no draft required. `projects` is an array of per-service
// pricePayload objects (camelCase: { postingType, tier, wordCount, ... }) and
// `discount` is an optional whole-order { discountType, discountValue }. Resolves
// to { total, discount, totalDiscounted, lines }. Pricing is best-effort: on any
// failure (422 "No projects supplied", network) we return a safe zero shape so the
// client-side estimate can cover the gap rather than blocking the wizard.
export async function priceProject(projects, discount = {}) {
  try {
    return await postJson("/post/price", "POST", { projects, ...discount })
  } catch {
    return { total: 0, discount: 0, totalDiscounted: 0, lines: [] }
  }
}

// Apply a discount code to the draft. Resolves to the updated draft (its
// `discount` field reflects the result).
export async function addDiscount(draftId, code) {
  return postJson(`/post/drafts/${draftId}/discount`, "POST", { code })
}

// Remove the draft's discount. Resolves to the updated draft.
export async function removeDiscount(draftId) {
  return postJson(`/post/drafts/${draftId}/discount`, "DELETE")
}

// Discard the draft. NOT navigational: all three surfaces (Projects drafts
// strip, /post entry split, dashboard resume banner) stay where they are and
// reload their own props in `onFinish`, so this must be a fetch — the server
// answers 204 and an Inertia visit would render that bodiless response in its
// error dialog (see the transport note above). `onFinish` runs exactly once
// whether the DELETE succeeds or fails, and the returned promise never rejects.
export function discardDraft(draftId, { onFinish } = {}) {
  return postJson(`/post/drafts/${draftId}`, "DELETE")
    .finally(() => onFinish?.())
    .catch(() => {})
}
